// Formatting utilities for numbers and values
// Based on frontend/src/services/formatting.service.ts

import type { FormatLocale } from './tenantConfig.service';

const DEFAULT_LOCALE: FormatLocale = {
  decimal: '.',
  thousands: ',',
  grouping: [3],
  currency: ['$', ''],
};

let _activeLocale: FormatLocale = DEFAULT_LOCALE;

/** Called by FormatLocaleSync to update the active locale from tenant config. */
export const setFormatLocale = (locale: FormatLocale) => {
  _activeLocale = locale;
};

export const getFormatLocale = (): FormatLocale => _activeLocale;

/**
 * Format a number using the active FormatLocale settings.
 */
const formatWithLocale = (num: number, decimals: number): string => {
  const locale = _activeLocale;
  const isNegative = num < 0;
  const absNum = Math.abs(num);

  // Split into integer and decimal parts
  const fixed = absNum.toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');

  // Apply grouping
  let grouped = '';
  const digits = intPart.split('');
  const groupSizes = locale.grouping;
  let groupIdx = 0;
  let remaining = digits.length;

  while (remaining > 0) {
    const groupSize = groupSizes[Math.min(groupIdx, groupSizes.length - 1)];
    const start = Math.max(remaining - groupSize, 0);
    const chunk = digits.slice(start, remaining).join('');
    grouped = grouped ? chunk + locale.thousands + grouped : chunk;
    remaining = start;
    groupIdx++;
  }

  let result = grouped;
  if (decimals > 0 && decPart) {
    result += locale.decimal + decPart;
  }

  if (isNegative) {
    result = '-' + result;
  }

  return result;
};

const formatNumber = (num: number, decimals: number = 2): string => {
  if (num == null || isNaN(num)) {
    return num?.toString() || '-';
  }

  return formatWithLocale(num, decimals);
};

export const integerFormatter = (num: number): string => {
  if (num == null) {
    return '-';
  }

  let formatted = decimalFormatter(Math.abs(num));
  if (isNaN(num)) {
    formatted = num.toString();
  }
  return formatted;
};

export const decimalFormatter = (num: number): string => {
  return formatNumber(num, 2);
};

export const formatFinancial = (num: number | string): string => {
  if (typeof num === 'string') {
    num = parseFloat(num);
  }

  const isNegative = num < 0;
  let formatted = decimalFormatter(Math.abs(num));

  if (num == null) {
    return '-';
  }
  if (isNaN(num)) {
    return num.toString();
  }

  const decChar = _activeLocale.decimal;

  // Ensure 2 decimal places
  if (formatted.indexOf(decChar) >= 0) {
    const parts = formatted.split(decChar);
    if (parts[1].length === 1) {
      formatted = formatted + '0';
    }
  } else {
    formatted = formatted + decChar + '00';
  }

  if (isNegative) {
    return '(' + formatted + ')';
  }
  return formatted;
};

export const formatPercentage = (num: number): string => {
  if (num == null) {
    return '-';
  }
  if (isNaN(num)) {
    return num.toString();
  }
  return decimalFormatter(Math.round(num * 10000) / 100) + '%';
};

export const summarizeNumber = (num: number): string => {
  if (num == null) {
    return '';
  }
  if (num < 1000) {
    return num.toString();
  } else if (num < 100000) {
    return ((Math.round(num / 100)) / 10).toString() + 'k';
  } else if (num < 1000000) {
    return Math.floor(num / 1000).toString() + 'k';
  } else if (num < 1000000000) {
    return (Math.round(num / 100000) / 10).toString() + 'M';
  } else if (num < 1000000000000) {
    return (Math.floor(num / 100000000) / 10).toString() + 'B';
  }
  return '';
};

interface FormatProps {
  value: any;
  format?: string;
}

export const formatValue = ({ value, format }: FormatProps): string => {
  if (value == null) {
    return '-';
  }

  let formattedValue: string;

  const fmt = (format || '').toUpperCase();
  switch (fmt) {
    case 'PERCENT':
      formattedValue = formatPercentage(value);
      break;
    case 'FINANCIAL':
    case 'CURRENCY':
      formattedValue = formatFinancial(value);
      break;
    case 'MULTIPLIER':
      formattedValue = (Math.round(value * 100) / 100).toFixed(2) + 'x';
      break;
    case 'INTEGER':
      formattedValue = formatNumber(Math.round(value), 0);
      break;
    case 'SUMMARIZE_NUMBER':
      formattedValue = summarizeNumber(value);
      break;
    case 'NUMERIC':
    case 'NUMBER':
      formattedValue = integerFormatter(value);
      break;
    case 'DECIMAL':
      formattedValue = decimalFormatter(value);
      break;
    default:
      formattedValue = value?.toString() || '-';
  }

  return formattedValue;
};

/**
 * Format a value for use as a tick label on a chart axis.
 * Uses summarizeNumber for NUMERIC/FINANCIAL formats.
 * For PERCENT, multiplies by 100, summarizes, then adds "%".
 */
export const formatTickValue = ({ value, format }: FormatProps): string => {
  if (value == null || isNaN(value)) {
    return value?.toString() || '-';
  }

  const fmt = (format || '').toUpperCase();
  switch (fmt) {
    case 'PERCENT':
      // Multiply by 100 to convert from decimal (0.02) to percentage (2)
      const percentValue = value * 100;
      const summarized = summarizeNumber(percentValue);
      return summarized + ' %';
    case 'FINANCIAL':
    case 'CURRENCY':
    case 'NUMERIC':
    case 'NUMBER':
    case 'DECIMAL':
    case 'INTEGER':
    case 'SUMMARIZE_NUMBER':
      return summarizeNumber(value);
    default:
      return summarizeNumber(value);
  }
};
